export const SURPLUS_LINES_TAX_ESTIMATOR_TERMS_VERSION = 'SLIP.Tax.Estimator.TC.2025';

export const SURPLUS_LINES_TAX_ESTIMATOR_TERMS_URL =
  'https://slipplus.com/wp-content/uploads/2025/06/SLIP.Tax_.Estimator.TC_.2025.pdf';

export const SURPLUS_LINES_TAX_ESTIMATOR_TERMS_TEXT =
  'This tax calculator is a tool for estimates only and does not provide legal or tax advice.';

export type TaxBase = 'premium' | 'premiumPlusFees';

/** Which transaction date selects the applicable rate schedule for a state. */
export type RateBasisDate = 'policyEffectiveDate' | 'endorsementEffectiveDate';

export type ExternalStateConfig = {
  stateName: string;
  stateCode: string;
  externalUrl: string;
  linkLabel: string;
  /** Short label shown on the state grid card (mockup: "SLIP +", "SLTX", etc.) */
  gridSubtitle: string;
};

/** Dated rate window. Dates are inclusive ISO YYYY-MM-DD. Omit expirationDate for open-ended. */
export type RateSchedule = {
  rate: number;
  effectiveDate: string;
  expirationDate?: string;
};

export type BuiltInCalculationLine = {
  label: string;
  /** Simple static rate (decimal). Ignored when `rates` is set. */
  rate?: number;
  /** Effective/expiration rate schedules. Prefer over `rate` when present. */
  rates?: RateSchedule[];
  flatAmount?: number;
  base?: TaxBase;
  /**
   * Display wording for the calculation basis (e.g. "Premium and Carrier Fees").
   * Defaults from `base` / state `defaultBase` when omitted.
   */
  baseLabel?: string;
  roundToNearestDollar?: boolean;
};

export type BuiltInStateConfig = {
  stateName: string;
  stateCode: string;
  defaultBase: TaxBase;
  /** Default display label for `defaultBase` when a line omits `baseLabel`. */
  defaultBaseLabel?: string;
  lines: BuiltInCalculationLine[];
  notes?: string;
  /** Extra wording appended to the Tax Rate / Fee Rate & Basis display (e.g. NJ fire split). */
  rateSummarySuffix?: string;
  /**
   * Which date drives rate-schedule selection.
   * Default: policyEffectiveDate (endorsements keep the parent policy rate).
   */
  rateBasisDate?: RateBasisDate;
  /** @deprecated Prefer `notes`. Kept for backward compatibility. */
  phase1Limitation?: string;
};

const SLIP_PLUS_TAX_ESTIMATOR_URL = 'https://slipplus.com/tax-estimator/';

const BASE_LABEL_PREMIUM = 'Premium Only';
const BASE_LABEL_FEES = 'Premium and Fees';
const BASE_LABEL_POLICY_FEES = 'Premium and Policy Fees';
const BASE_LABEL_CARRIER_FEES = 'Premium and Carrier Fees';
const BASE_LABEL_ALLOWABLE_FEES = 'Premium and Allowable Fees';

/** States with an official external tax estimator — link out instead of built-in calculator. */
export const EXTERNAL_STATES: ExternalStateConfig[] = [
  { stateName: 'Alabama', stateCode: 'AL', externalUrl: SLIP_PLUS_TAX_ESTIMATOR_URL, linkLabel: 'Tax Estimator - SLIP+', gridSubtitle: 'SLIP +' },
  { stateName: 'California', stateCode: 'CA', externalUrl: 'https://www.slacal.com/resources/broker-tax-stamping-fee-estimator', linkLabel: 'Broker Tax & Stamping Fee Estimator', gridSubtitle: 'SLACAL' },
  { stateName: 'Colorado', stateCode: 'CO', externalUrl: SLIP_PLUS_TAX_ESTIMATOR_URL, linkLabel: 'Tax Estimator - SLIP+', gridSubtitle: 'SLIP +' },
  { stateName: 'Florida', stateCode: 'FL', externalUrl: 'https://www.fslso.com/tax-estimator', linkLabel: 'Tax Estimator | Florida Surplus Lines Service Office', gridSubtitle: 'FSLSO' },
  { stateName: 'Illinois', stateCode: 'IL', externalUrl: 'https://www.slai.org/sl-tax-calculator/', linkLabel: 'SL Tax Calculator – Surplus Line Association of Illinois', gridSubtitle: 'SLAI' },
  { stateName: 'Kansas', stateCode: 'KS', externalUrl: SLIP_PLUS_TAX_ESTIMATOR_URL, linkLabel: 'Tax Estimator - SLIP+', gridSubtitle: 'SLIP +' },
  { stateName: 'Louisiana', stateCode: 'LA', externalUrl: SLIP_PLUS_TAX_ESTIMATOR_URL, linkLabel: 'Tax Estimator - SLIP+', gridSubtitle: 'SLIP +' },
  { stateName: 'Mississippi', stateCode: 'MS', externalUrl: 'https://www.msla.org/tax-estimator', linkLabel: 'MSLA Tax Estimator', gridSubtitle: 'MSLA' },
  { stateName: 'Montana', stateCode: 'MT', externalUrl: SLIP_PLUS_TAX_ESTIMATOR_URL, linkLabel: 'Tax Estimator - SLIP+', gridSubtitle: 'SLIP +' },
  { stateName: 'Oklahoma', stateCode: 'OK', externalUrl: SLIP_PLUS_TAX_ESTIMATOR_URL, linkLabel: 'Tax Estimator - SLIP+', gridSubtitle: 'SLIP +' },
  { stateName: 'Oregon', stateCode: 'OR', externalUrl: 'https://slip.oregonsla.org/PublicPages/Resources/TaxCalculator.aspx', linkLabel: 'Tax Calculator', gridSubtitle: 'OSLA' },
  { stateName: 'South Dakota', stateCode: 'SD', externalUrl: SLIP_PLUS_TAX_ESTIMATOR_URL, linkLabel: 'Tax Estimator - SLIP+', gridSubtitle: 'SLIP +' },
  { stateName: 'Tennessee', stateCode: 'TN', externalUrl: SLIP_PLUS_TAX_ESTIMATOR_URL, linkLabel: 'Tax Estimator - SLIP+', gridSubtitle: 'SLIP +' },
  { stateName: 'Texas', stateCode: 'TX', externalUrl: 'https://www.sltx.org/brokers/tax-estimator/', linkLabel: 'Tax Estimator | SLTX', gridSubtitle: 'SLTX' },
  { stateName: 'Washington', stateCode: 'WA', externalUrl: 'https://surpluslines.org/brokerages/filing-information/tax-calculator/', linkLabel: 'Tax Calculator – Surplus Line Association of Washington', gridSubtitle: 'SLAW' },
  { stateName: 'Wyoming', stateCode: 'WY', externalUrl: SLIP_PLUS_TAX_ESTIMATOR_URL, linkLabel: 'Tax Estimator - SLIP+', gridSubtitle: 'SLIP +' },
];

/** States without an external calculator — use built-in Texas-style estimator. */
export const BUILT_IN_STATES: BuiltInStateConfig[] = [
  {
    stateName: 'Alaska',
    stateCode: 'AK',
    defaultBase: 'premiumPlusFees',
    defaultBaseLabel: BASE_LABEL_POLICY_FEES,
    lines: [
      { label: 'Surplus Lines Tax', rate: 0.027 },
      { label: 'Stamping Fee', rate: 0.01 },
    ],
  },
  {
    stateName: 'Arizona',
    stateCode: 'AZ',
    defaultBase: 'premiumPlusFees',
    lines: [
      { label: 'Surplus Lines Tax', rate: 0.03 },
      { label: 'Stamping Fee', rate: 0.002 },
    ],
  },
  {
    stateName: 'Arkansas',
    stateCode: 'AR',
    defaultBase: 'premiumPlusFees',
    lines: [{ label: 'Surplus Lines Tax', rate: 0.04 }],
  },
  {
    stateName: 'Connecticut',
    stateCode: 'CT',
    defaultBase: 'premium',
    defaultBaseLabel: BASE_LABEL_PREMIUM,
    lines: [
      { label: 'Surplus Lines Tax', rate: 0.04, base: 'premium' },
    ],
  },
  {
    stateName: 'Delaware',
    stateCode: 'DE',
    defaultBase: 'premiumPlusFees',
    lines: [{ label: 'Surplus Lines Tax', rate: 0.03 }],
  },
  {
    stateName: 'District of Columbia',
    stateCode: 'DC',
    defaultBase: 'premiumPlusFees',
    lines: [{ label: 'Surplus Lines Tax', rate: 0.02 }],
  },
  {
    stateName: 'Georgia',
    stateCode: 'GA',
    defaultBase: 'premiumPlusFees',
    lines: [{ label: 'Surplus Lines Tax', rate: 0.04 }],
  },
  {
    stateName: 'Hawaii',
    stateCode: 'HI',
    defaultBase: 'premiumPlusFees',
    defaultBaseLabel: BASE_LABEL_CARRIER_FEES,
    lines: [{ label: 'Surplus Lines Tax', rate: 0.0468 }],
  },
  {
    stateName: 'Idaho',
    stateCode: 'ID',
    defaultBase: 'premiumPlusFees',
    lines: [
      { label: 'Surplus Lines Tax', rate: 0.015, roundToNearestDollar: true },
      { label: 'Stamping Fee', rate: 0.005, roundToNearestDollar: true },
    ],
    notes:
      'Stamping fees are fully earned and not returned unless the policy is canceled flat. On pro-rata cancellations, stamping fee is not returned; on flat cancellations, it is returned.',
  },
  {
    stateName: 'Indiana',
    stateCode: 'IN',
    defaultBase: 'premiumPlusFees',
    lines: [{ label: 'Surplus Lines Tax', rate: 0.025 }],
  },
  {
    stateName: 'Iowa',
    stateCode: 'IA',
    defaultBase: 'premiumPlusFees',
    defaultBaseLabel: BASE_LABEL_ALLOWABLE_FEES,
    rateBasisDate: 'endorsementEffectiveDate',
    lines: [
      {
        label: 'Surplus Lines Tax',
        rates: [
          { rate: 0.00925, effectiveDate: '2026-01-01', expirationDate: '2026-12-31' },
          { rate: 0.009, effectiveDate: '2027-01-01' },
        ],
      },
    ],
    notes:
      'Senate File 549. Surplus Lines Tax Reduction Notice. For the 2026 calendar year, nine hundred seventy-five thousands of one percent. For the 2027 calendar year and subsequent calendar years, nine-tenths of one percent.',
  },
  {
    stateName: 'Kentucky',
    stateCode: 'KY',
    defaultBase: 'premiumPlusFees',
    lines: [
      { label: 'Surplus Lines Tax', rate: 0.03 },
      { label: 'Surcharge', rate: 0.018 },
    ],
    notes:
      'The calculations below do not include Municipal Taxes. Review the annual KY Local Government Premium Tax Schedule and calculate the applicable municipal taxes for each risk/location. Add and charge the appropriate municipal tax amount(s) based on the risk locations.',
  },
  {
    stateName: 'Maine',
    stateCode: 'ME',
    defaultBase: 'premiumPlusFees',
    lines: [{ label: 'Surplus Lines Tax', rate: 0.03 }],
  },
  {
    stateName: 'Maryland',
    stateCode: 'MD',
    defaultBase: 'premiumPlusFees',
    lines: [{ label: 'Surplus Lines Tax', rate: 0.03 }],
  },
  {
    stateName: 'Massachusetts',
    stateCode: 'MA',
    defaultBase: 'premium',
    defaultBaseLabel: BASE_LABEL_PREMIUM,
    lines: [{ label: 'Surplus Lines Tax', rate: 0.04, base: 'premium' }],
  },
  {
    stateName: 'Michigan',
    stateCode: 'MI',
    defaultBase: 'premium',
    defaultBaseLabel: BASE_LABEL_PREMIUM,
    lines: [{ label: 'Surplus Lines Tax', rate: 0.025, base: 'premium' }],
  },
  {
    stateName: 'Minnesota',
    stateCode: 'MN',
    defaultBase: 'premiumPlusFees',
    lines: [
      { label: 'Surplus Lines Tax', rate: 0.03 },
      { label: 'Stamping Fee', rate: 0.0004 },
    ],
  },
  {
    stateName: 'Missouri',
    stateCode: 'MO',
    defaultBase: 'premiumPlusFees',
    lines: [{ label: 'Surplus Lines Tax', rate: 0.05 }],
  },
  {
    stateName: 'Nebraska',
    stateCode: 'NE',
    defaultBase: 'premiumPlusFees',
    lines: [{ label: 'Surplus Lines Tax', rate: 0.03 }],
  },
  {
    stateName: 'Nevada',
    stateCode: 'NV',
    defaultBase: 'premiumPlusFees',
    lines: [
      { label: 'Surplus Lines Tax', rate: 0.035 },
      { label: 'Stamping Fee', rate: 0.004 },
    ],
  },
  {
    stateName: 'New Hampshire',
    stateCode: 'NH',
    defaultBase: 'premium',
    defaultBaseLabel: BASE_LABEL_PREMIUM,
    lines: [{ label: 'Surplus Lines Tax', rate: 0.03, base: 'premium' }],
  },
  {
    stateName: 'New Jersey',
    stateCode: 'NJ',
    defaultBase: 'premium',
    defaultBaseLabel: BASE_LABEL_PREMIUM,
    rateBasisDate: 'policyEffectiveDate',
    lines: [{ label: 'Surplus Lines Tax', rate: 0.05, base: 'premium' }],
    rateSummarySuffix:
      'Policies that include fire: 3% on the fire-only portion and 2% on all other',
  },
  {
    stateName: 'New Mexico',
    stateCode: 'NM',
    defaultBase: 'premiumPlusFees',
    lines: [{ label: 'Surplus Lines Tax', rate: 0.03003 }],
  },
  {
    stateName: 'New York',
    stateCode: 'NY',
    defaultBase: 'premiumPlusFees',
    defaultBaseLabel: BASE_LABEL_CARRIER_FEES,
    rateBasisDate: 'policyEffectiveDate',
    lines: [
      { label: 'Surplus Lines Tax', rate: 0.036 },
      { label: 'Stamping Fee', rate: 0.0015 },
    ],
  },
  {
    stateName: 'North Carolina',
    stateCode: 'NC',
    defaultBase: 'premiumPlusFees',
    rateBasisDate: 'policyEffectiveDate',
    lines: [
      {
        label: 'Surplus Lines Tax',
        rate: 0.05,
        base: 'premiumPlusFees',
        baseLabel: BASE_LABEL_FEES,
      },
      {
        label: 'Stamping Fee',
        rate: 0.003,
        base: 'premiumPlusFees',
        baseLabel: BASE_LABEL_CARRIER_FEES,
      },
    ],
  },
  {
    stateName: 'North Dakota',
    stateCode: 'ND',
    defaultBase: 'premiumPlusFees',
    lines: [{ label: 'Surplus Lines Tax', rate: 0.0175 }],
  },
  {
    stateName: 'Ohio',
    stateCode: 'OH',
    defaultBase: 'premiumPlusFees',
    defaultBaseLabel: BASE_LABEL_CARRIER_FEES,
    lines: [{ label: 'Surplus Lines Tax', rate: 0.05 }],
  },
  {
    stateName: 'Pennsylvania',
    stateCode: 'PA',
    defaultBase: 'premiumPlusFees',
    lines: [
      { label: 'Surplus Lines Tax', rate: 0.06 },
      { label: 'Stamping Fee', flatAmount: 20 },
    ],
    notes: 'Stamping fee is a $20 flat fee per policy (new and renewal) and is fully earned.',
  },
  {
    stateName: 'Rhode Island',
    stateCode: 'RI',
    defaultBase: 'premium',
    defaultBaseLabel: BASE_LABEL_PREMIUM,
    lines: [{ label: 'Surplus Lines Tax', rate: 0.04, base: 'premium' }],
  },
  {
    stateName: 'South Carolina',
    stateCode: 'SC',
    defaultBase: 'premiumPlusFees',
    lines: [{ label: 'Surplus Lines Tax', rate: 0.06 }],
  },
  {
    stateName: 'Utah',
    stateCode: 'UT',
    defaultBase: 'premiumPlusFees',
    lines: [
      { label: 'Surplus Lines Tax', rate: 0.0425 },
      { label: 'Stamping Fee', rate: 0.0018 },
    ],
  },
  {
    stateName: 'Vermont',
    stateCode: 'VT',
    defaultBase: 'premium',
    defaultBaseLabel: BASE_LABEL_PREMIUM,
    lines: [{ label: 'Surplus Lines Tax', rate: 0.03, base: 'premium' }],
  },
  {
    stateName: 'Virginia',
    stateCode: 'VA',
    defaultBase: 'premiumPlusFees',
    lines: [
      { label: 'Surplus Lines Tax', rate: 0.0225 },
      { label: 'Assessment Fee', rate: 0.00035 },
    ],
  },
  {
    stateName: 'West Virginia',
    stateCode: 'WV',
    defaultBase: 'premiumPlusFees',
    lines: [{ label: 'Surplus Lines Tax', rate: 0.0455 }],
  },
  {
    stateName: 'Wisconsin',
    stateCode: 'WI',
    defaultBase: 'premiumPlusFees',
    lines: [{ label: 'Surplus Lines Tax', rate: 0.03 }],
  },
];

const externalByName = new Map(EXTERNAL_STATES.map((s) => [s.stateName, s]));
const builtInByName = new Map(BUILT_IN_STATES.map((s) => [s.stateName, s]));

export type EstimatorGridItem = {
  stateName: string;
  stateCode: string;
  stateId?: number;
  kind: EstimatorStateKind;
  gridSubtitle?: string;
};

/** Merge estimator grid rows with `/States/getStates` ids (match on postal abbreviation). */
export const mergeEstimatorGridWithStateIds = (
  items: EstimatorGridItem[],
  apiStates: { stateId: number; stateName?: string; postalAbbv?: string }[],
): EstimatorGridItem[] => {
  const byCode = new Map(
    apiStates
      .filter((state) => state.postalAbbv)
      .map((state) => [state.postalAbbv!.trim().toUpperCase(), state.stateId]),
  );
  const byName = new Map(
    apiStates
      .filter((state) => state.stateName)
      .map((state) => [state.stateName!.trim().toLowerCase(), state.stateId]),
  );

  return items.map((item) => ({
    ...item,
    stateId:
      byCode.get(item.stateCode.trim().toUpperCase())
      ?? byName.get(item.stateName.trim().toLowerCase()),
  }));
};

/** All states for the landing grid, sorted alphabetically (design mockup page 4). */
export const getEstimatorGridItems = (): EstimatorGridItem[] => [
  ...EXTERNAL_STATES.map((s) => ({
    stateName: s.stateName,
    stateCode: s.stateCode,
    kind: 'external' as const,
    gridSubtitle: s.gridSubtitle,
  })),
  ...BUILT_IN_STATES.map((s) => ({
    stateName: s.stateName,
    stateCode: s.stateCode,
    kind: 'builtIn' as const,
  })),
].sort((a, b) => a.stateName.localeCompare(b.stateName));

export type EstimatorStateKind = 'external' | 'builtIn' | 'unavailable';

export const getEstimatorStateKind = (stateName: string): EstimatorStateKind => {
  if (!stateName) return 'unavailable';
  if (externalByName.has(stateName)) return 'external';
  if (builtInByName.has(stateName)) return 'builtIn';
  return 'unavailable';
};

export const getExternalStateConfig = (stateName: string): ExternalStateConfig | undefined =>
  externalByName.get(stateName);

export const getBuiltInStateConfig = (stateName: string): BuiltInStateConfig | undefined =>
  builtInByName.get(stateName);

/** All states that appear in either external or built-in lists, sorted by name. */
export const ESTIMATOR_STATE_OPTIONS = [
  ...EXTERNAL_STATES.map((s) => s.stateName),
  ...BUILT_IN_STATES.map((s) => s.stateName),
].sort((a, b) => a.localeCompare(b));

export const getStateCodeByName = (stateName: string): string | undefined => {
  return externalByName.get(stateName)?.stateCode ?? builtInByName.get(stateName)?.stateCode;
};

export const getDefaultBaseLabel = (base: TaxBase, override?: string): string => {
  if (override) return override;
  return base === 'premium' ? BASE_LABEL_PREMIUM : BASE_LABEL_FEES;
};

/** Format a decimal rate as a percent string (e.g. 0.027 → "2.7%"). */
export const formatRatePercent = (rate: number): string => {
  const pct = rate * 100;
  const rounded = Number(pct.toFixed(4));
  return `${rounded}%`;
};

const formatShortDate = (isoDate: string): string => {
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) return isoDate;
  return `${Number(month)}/${Number(day)}/${year}`;
};

const formatScheduleWindow = (schedule: RateSchedule): string => {
  const start = formatShortDate(schedule.effectiveDate);
  if (!schedule.expirationDate) {
    return `eff. ${start}+`;
  }
  return `eff. ${start}–${formatShortDate(schedule.expirationDate)}`;
};

/**
 * Resolve the applicable rate for a line on a given as-of date (ISO YYYY-MM-DD).
 * When `asOfDate` is omitted, uses today's date for scheduled rates.
 */
export const resolveLineRate = (
  line: BuiltInCalculationLine,
  asOfDate?: string,
): number | undefined => {
  if (line.flatAmount != null && line.rate == null && !line.rates?.length) {
    return undefined;
  }

  if (line.rates?.length) {
    const date = asOfDate ?? new Date().toISOString().slice(0, 10);
    const inWindow = line.rates
      .filter((schedule) => (
        schedule.effectiveDate <= date
        && (!schedule.expirationDate || schedule.expirationDate >= date)
      ))
      .sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));

    if (inWindow.length > 0) {
      return inWindow[0].rate;
    }

    const prior = line.rates
      .filter((schedule) => schedule.effectiveDate <= date)
      .sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));
    if (prior.length > 0) {
      return prior[0].rate;
    }

    const earliest = [...line.rates].sort((a, b) =>
      a.effectiveDate.localeCompare(b.effectiveDate),
    );
    return earliest[0]?.rate;
  }

  return line.rate;
};

/** Pick the as-of date from inputs using the state's configured rate basis. */
export const getRateAsOfDate = (
  config: BuiltInStateConfig,
  inputs: { policyEffectiveDate?: string; endorsementEffectiveDate?: string },
): string | undefined => {
  const basis = config.rateBasisDate ?? 'policyEffectiveDate';
  if (basis === 'endorsementEffectiveDate') {
    return inputs.endorsementEffectiveDate || inputs.policyEffectiveDate;
  }
  return inputs.policyEffectiveDate || inputs.endorsementEffectiveDate;
};

const formatLineRatePart = (
  line: BuiltInCalculationLine,
  asOfDate?: string,
): string | null => {
  if (line.flatAmount != null) {
    return `$${line.flatAmount.toFixed(line.flatAmount % 1 === 0 ? 0 : 2)} flat`;
  }

  if (line.rates?.length) {
    if (asOfDate) {
      const resolved = resolveLineRate(line, asOfDate);
      if (resolved == null) return null;
      return formatRatePercent(resolved);
    }
    return line.rates
      .map((schedule) => `${formatRatePercent(schedule.rate)} (${formatScheduleWindow(schedule)})`)
      .join(' / ');
  }

  if (line.rate == null) return null;
  return formatRatePercent(line.rate);
};

/**
 * Display string for programmed (built-in) states, e.g.
 * "SL Tax 2.7% on Premium and Policy Fees; Stamping Fee 1% on Premium and Policy Fees."
 */
export const formatBuiltInRateSummary = (
  config: BuiltInStateConfig,
  asOfDate?: string,
): string => {
  const parts = config.lines.map((line) => {
    const ratePart = formatLineRatePart(line, asOfDate);
    if (!ratePart) return null;
    const base = line.base ?? config.defaultBase;
    const baseLabel = getDefaultBaseLabel(
      base,
      line.baseLabel ?? config.defaultBaseLabel,
    );
    const shortLabel = line.label
      .replace(/^Surplus Lines Tax$/i, 'SL Tax')
      .replace(/^Stamping Fees?$/i, 'Stamping Fee');
    if (line.flatAmount != null) {
      return `${shortLabel} ${ratePart}`;
    }
    return `${shortLabel} ${ratePart} on ${baseLabel}`;
  }).filter((part): part is string => Boolean(part));

  if (config.rateSummarySuffix) {
    parts.push(config.rateSummarySuffix);
  }

  return parts.join('; ') + (parts.length > 0 ? '.' : '');
};
